import { useState } from "react";
import { Plus } from "lucide-react";
import { Topbar } from "@marekl/adminer/shell";
import {
  Page,
  Table,
  Button,
  Input,
  Select,
  Modal,
  FormStack,
  Pagination,
  Alert,
  FilterField,
  FilterPanel,
  DateTimeField,
  TextField,
} from "@marekl/adminer/components";
import { useDebouncedSearchFilter } from "@marekl/adminer/hooks";
import { useAuth } from "../../auth/useAuth";
import { formatCurrency } from "@marekl/adminer/format";
import {
  useItemList,
  useCreateItem,
  useUpdateItem,
  useDeleteItem,
  type Item,
  type ItemInput,
} from "../../lib/queries/items";
import { useCategoryList, type Category } from "../../lib/queries/categories";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; item: Item }
  | { kind: "delete"; item: Item };

export function ItemsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "manager" || user?.role === "superadmin";
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState<string>("");
  const [stack, setStack] = useState<string[]>([]);
  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => {
      setQ(next);
      setCursor("");
      setStack([]);
    },
  });
  const cats = useCategoryList({ per_page: 200 });
  const [categoryId, setCategoryId] = useState<string>("");
  const { data, isLoading } = useItemList({
    q,
    cursor: cursor || undefined,
    category_id: categoryId ? Number(categoryId) : undefined,
  });
  const items = data?.items ?? [];
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const close = () => setModal({ kind: "closed" });

  function goNext() {
    if (!data?.next_cursor) return;
    setStack((s) => [...s, cursor]);
    setCursor(data.next_cursor);
  }
  function goPrev() {
    const s = [...stack];
    setCursor(s.pop() ?? "");
    setStack(s);
  }

  return (
    <>
      <Topbar crumbs={[{ label: "Položky" }]} />
      <Page.Content stack gap="lg">
        <Page.Header
          title="Položky"
          subtitle="správa položek katalogu"
          actions={
            canEdit && (
              <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                <Plus size={16} aria-hidden />
                Přidat položku
              </Button>
            )
          }
        />

        <FilterPanel>
          <FilterField wide>
            <Input
              label="Hledat"
              type="search"
              value={qDraft}
              placeholder="název nebo popis"
              onChange={(e) => setQDraft(e.target.value)}
            />
          </FilterField>
          <FilterField>
            <Select
              label="Kategorie"
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setCursor("");
                setStack([]);
              }}
            >
              <option value="">Všechny</option>
              {(cats.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FilterField>
        </FilterPanel>

        <Page.SectionHeader
          title="Položky"
          meta={data ? `přibližně ${data.total_estimate}` : undefined}
        />

        <Table<Item>
          rowKey={(i) => i.id}
          rows={items}
          empty={isLoading ? "Načítám…" : "Žádné položky."}
          columns={[
            { key: "name", header: "Název", render: (i) => i.name },
            {
              key: "category",
              header: "Kategorie",
              render: (i) => <TextField record={i} source="category.name" />,
            },
            { key: "qty", header: "Počet", render: (i) => i.quantity },
            {
              key: "price",
              header: "Cena",
              render: (i) => formatCurrency(i.price_cents / 100),
            },
            {
              key: "created",
              header: "Vytvořeno",
              render: (i) => <DateTimeField record={i} source="created_at" />,
            },
            ...(canEdit
              ? [
                  {
                    key: "actions",
                    header: "",
                    render: (i: Item) => (
                      <Table.Actions>
                        <Table.Action onClick={() => setModal({ kind: "edit", item: i })}>
                          Upravit
                        </Table.Action>
                        <Table.Action onClick={() => setModal({ kind: "delete", item: i })}>
                          Smazat
                        </Table.Action>
                      </Table.Actions>
                    ),
                  },
                ]
              : []),
          ]}
        />

        <Pagination
          hasPrev={stack.length > 0}
          hasNext={!!data?.next_cursor}
          onPrev={goPrev}
          onNext={goNext}
          summary={data ? `Přibližně ${data.total_estimate} položek` : undefined}
          prevLabel="Předchozí"
          nextLabel="Další"
        />
      </Page.Content>

      {modal.kind === "create" && (
        <ItemFormModal categories={cats.data?.items ?? []} onClose={close} />
      )}
      {modal.kind === "edit" && (
        <ItemFormModal
          initial={modal.item}
          categories={cats.data?.items ?? []}
          onClose={close}
        />
      )}
      {modal.kind === "delete" && (
        <DeleteItemModal item={modal.item} onClose={close} />
      )}
    </>
  );
}

function ItemFormModal({
  initial,
  categories,
  onClose,
}: {
  initial?: Item;
  categories: Category[];
  onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [categoryId, setCategoryId] = useState<number>(
    initial?.category_id ?? categories[0]?.id ?? 0,
  );
  const [quantity, setQuantity] = useState<number>(initial?.quantity ?? 0);
  const [price, setPrice] = useState<string>(
    initial ? (initial.price_cents / 100).toFixed(2) : "0",
  );
  const [error, setError] = useState<string | null>(null);
  const create = useCreateItem();
  const update = useUpdateItem();
  const isEdit = !!initial;
  const isPending = create.isPending || update.isPending;

  async function submit() {
    setError(null);
    const input: ItemInput = {
      category_id: categoryId,
      name,
      notes,
      quantity,
      price_cents: Math.round(parseFloat(price.replace(",", ".")) * 100) || 0,
    };
    try {
      if (isEdit) {
        await update.mutateAsync({ id: initial!.id, ...input });
      } else {
        await create.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Uložení se nezdařilo");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Upravit položku" : "Nová položka"}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Zrušit</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={isPending || !name.trim() || !categoryId}
          >
            Uložit
          </Button>
        </>
      }
    >
      <FormStack>
        {error && <Alert variant="err">{error}</Alert>}
        <Input
          label="Název"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Select
          label="Kategorie"
          value={String(categoryId)}
          onChange={(e) => setCategoryId(Number(e.target.value))}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Input
          label="Počet"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
        />
        <Input
          label="Cena (Kč)"
          type="text"
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <Input
          label="Poznámky"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormStack>
    </Modal>
  );
}

function DeleteItemModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const del = useDeleteItem();
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await del.mutateAsync(item.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Smazání se nezdařilo");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Smazat položku"
      footer={
        <>
          <Button variant="default" onClick={onClose}>Zrušit</Button>
          <Button variant="danger" onClick={submit} disabled={del.isPending}>
            Smazat
          </Button>
        </>
      }
    >
      <FormStack>
        {error && <Alert variant="err">{error}</Alert>}
        <p>Opravdu chcete smazat položku <strong>{item.name}</strong>?</p>
      </FormStack>
    </Modal>
  );
}
